import { configWithUserFileToken, configWithUserToken } from "../../services/config";
import { ITicket } from "../models/ITicket";

export interface ITicketDashboardStats {
  total: number;
  mine: number;
  waitingForAssignment: number;
  assignedToMe: number;
  statusCounts: { statusId: string; statusName: string; count: number }[];
  departmentCounts: { departmentId: string; departmentName: string; count: number }[];
}

export const getTicketDashboardStatistics = () =>
  configWithUserToken.get<ITicketDashboardStats>("Ticket/GetTicketDashboardStatistics");

export const getMyTickets = (pageIndex = 0, pageSize = 50) =>
  configWithUserToken.get<{ paging: { pageIndex: number; totalCount: number; totalPages: number; hasNextPage: boolean; hasPreviousPage: boolean; pageSize: number }; items: any[] }>(
    `Ticket/GetMyTickets?pageIndex=${pageIndex}&pageSize=${pageSize}`
  );

export const getTicketById = (ticketId: string) =>
  configWithUserToken.get(`Ticket/GetTicket/${ticketId}`);

export interface ITicketListParams {
  statusId?: string;
  departmentId?: string;
  fromDate?: string;
  toDate?: string;
  searchTerm?: string;
  pageIndex?: number;
  pageSize?: number;
}

export const getAllTickets = (params: ITicketListParams = {}) => {
  const p = new URLSearchParams();
  if (params.statusId)     p.append("StatusId",     params.statusId);
  if (params.departmentId) p.append("DepartmentId", params.departmentId);
  if (params.fromDate)     p.append("FromDate",      params.fromDate);
  if (params.toDate)       p.append("ToDate",        params.toDate);
  if (params.searchTerm)   p.append("SearchTerm",    params.searchTerm);
  p.append("pageIndex", String(params.pageIndex ?? 0));
  p.append("pageSize",  String(params.pageSize  ?? 50));
  return configWithUserToken.get<{ paging: { pageIndex: number; totalCount: number; totalPages: number; hasNextPage: boolean; hasPreviousPage: boolean; pageSize: number }; items: any[] }>(
    `Ticket/GetAllTickets?${p.toString()}`
  );
};

export const getMessages = (ticketId: string) =>
  configWithUserToken.get(`Ticket/GetMessages/${ticketId}`);

export const addMessage = (ticketId: string, message: string, image?: File, bgColor?: string) => {
  const formData = new FormData();
  formData.append("TicketId", ticketId);
  formData.append("Message", message);
  if (image) formData.append("Image", image);
  if (bgColor) formData.append("BgColor", bgColor);
  return configWithUserFileToken.post("Ticket/AddMessageWithImage", formData);
};

const SUPPORT_ROLE_ID = "8a4f28f7-2b6c-ef11-986b-5cef2cd6e8d8";

export const getUserListByRole = () =>
  configWithUserToken.get(`Auth/UserUserListByRoleIds?roleIds=${SUPPORT_ROLE_ID}`);

export const reassignTicket = (ticketId: string, newCustomerId: string) =>
  configWithUserToken.put("Ticket/ReassignTicket", { ticketId, newCustomerId });

export const getAllDepartments = () =>
  configWithUserToken.get("Ticket/GetAllDepartments");

export const getAllStatuses = () =>
  configWithUserToken.get("Ticket/GetAllStatuses");

export const updateTicketStatus = (ticketId: string, ticketStatusId: string) =>
  configWithUserToken.put("Ticket/UpdateTicketStatus", { ticketId, ticketStatusId });

export const getAllTicketSubjects = () =>
  configWithUserToken.get<{ id: string; name: string; description: string; createdAt?: string }[]>("Ticket/GetAllTicketSubjects");

// Backend'de yalnızca SuperAdmin oluşturabilir. Konu düzenleme/silme servisi henüz yok.
export const createTicketSubject = (data: { name: string; description?: string }) =>
  configWithUserToken.post<{ id: string; name: string; description: string; createdAt: string }>("Ticket/CreateTicketSubject", data);

export interface ITicketTask {
  title: string;
  weight: number;
}

export const createTicket = (payload: {
  title: string;
  note: string;
  ticketStatusId: string;
  ticketDepartmentId: string;
  ticketSubjectId?: string;
  priority: number;
  tasks?: ITicketTask[];
}) => configWithUserToken.post("Ticket/CreateTicket", payload);

export const setTicketTaskCompletion = (payload: { taskId: string; isCompleted: boolean }) => {
  const p = new URLSearchParams();
  p.append("api-version", "1.0");
  return configWithUserToken.put(`Ticket/SetTicketTaskCompletion?${p.toString()}`, payload, {
    headers: { "X-Api-Version": "1.0" },
  });
};

export const reorderTicketTasks = (payload: { ticketId: string; taskIds: string[] }) => {
  const p = new URLSearchParams();
  p.append("api-version", "1.0");
  return configWithUserToken.put(`Ticket/ReorderTicketTasks?${p.toString()}`, payload, {
    headers: { "X-Api-Version": "1.0" },
  });
};

export const updateTicketTask = (payload: { taskId: string; title: string; weight: number }) => {
  const p = new URLSearchParams();
  p.append("api-version", "1.0");
  return configWithUserToken.put(`Ticket/UpdateTicketTask?${p.toString()}`, payload, {
    headers: { "X-Api-Version": "1.0" },
  });
};

export const addTicketTask = (payload: { title: string; weight: number; ticketId: string }) => {
  const p = new URLSearchParams();
  p.append("api-version", "1.0");
  return configWithUserToken.post(`Ticket/AddTicketTask?${p.toString()}`, payload, {
    headers: { "X-Api-Version": "1.0" },
  });
};

export const deleteTicket = (ticketId: string) =>
  configWithUserToken.delete(`Ticket/DeleteTicket/${ticketId}`);

export const updateTicket = (payload: {
  ticketId: string;
  title: string;
  note: string;
  ticketDepartmentId: string;
  ticketSubjectId?: string;
}) => configWithUserToken.put("Ticket/UpdateTicket", payload);

export const assignUsersToTicket = (ticketId: string, userIds: string[]) =>
  configWithUserToken.post("Ticket/AssignUsersToTicket", { ticketId, userIds });

export const getMyDepartments = () =>
  configWithUserToken.get<{ id: string; name: string }[]>("Ticket/GetUserDepartments");

export const getDepartmentUsers = (departmentId: string) =>
  configWithUserToken.get<string[]>(`Ticket/GetDepartmentUsers/${departmentId}`);

export interface ITicketDocument {
  id: string;
  ticketId: string;
  name: string;
  documentUrl: string;
  uploadDate: string;
}

export const getTicketDocuments = (ticketId: string) =>
  configWithUserToken.get<ITicketDocument[]>(`Ticket/GetTicketDocuments/${ticketId}`);

export const uploadTicketDocuments = (ticketId: string, files: File[]) => {
  const formData = new FormData();
  formData.append("TicketId", ticketId);
  files.forEach((f) => formData.append("Documents", f));
  return configWithUserFileToken.post("Ticket/UploadTicketDocuments", formData);
};

export const linkTicket = (payload: { ticketId: string; parentTicketId: string; linkType: number }) =>
  configWithUserToken.post("Ticket/Link", payload);

export const getTicketChildren = (ticketId: string) =>
  configWithUserToken.get<ITicket[]>(`Ticket/${ticketId}/Children`);
